import { Day } from '../../../interfaces';
import {
  ClassBasicResponse,
  CourseTeacher,
  ExcuseDefinitionResponse,
  GroupRole,
  Person,
} from '../../api/v2';

export interface AzureGroup {
  id: string;
  name: string;
  mailAddress?: string;
  role?: GroupRole;
  timePeriod?: string;
}

/**
 * Contains basic contact information for the institution.
 *
 * @export
 * @interface SchoolContactDetails
 */
export interface SchoolContactDetails {
  name: string;
  address: string;
  zipCode: string;
  city: string;
  countryCode: string;
  phone: string;
  mailAddress: string;
  website?: string;
  federalState?: string;
}

/**
 * Contains information about head teachers and school management.
 *
 * @export
 * @interface SchoolManagement
 */
export interface SchoolManagement {
  headTeachers: Person[];
  management: Person[];
}

/**
 * Describes if and when a sign-off is required.
 *
 * @enum {number}
 */
enum SignOffFrequency {
  /**
   * No audit required.
   */
  None,
  /**
   * Audit required on the first lesson of the day.
   */
  FirstLesson,
  /**
   * Audit required on the beginning of every lesson.
   */
  EveryLesson,
}

/**
 * Describes how much history data will be visible in the classbook view.
 *
 * @enum {number}
 */
export enum AbsenceHistorySummary {
  /**
   * Shows history from the beginning of the current week.
   */
  Week = 0,
  /**
   * Shows history from the beginning of the current (school) year.
   */
  Year = 1,
  /**
   * Shows history of the last two weeks.
   */
  Biweekly = 2,
  /**
   * Shows history of the last month.
   */
  Month = 3,
}

/**
 * Contains settings about absences sign-offs..
 * E.g.
 * - Should an absence get audited?
 * - When should an absence get audited?
 * - How long in the past should it be possible to audit an absence?
 *
 * @export
 * @interface AbsenceSignOffPolicy
 */
export interface AbsenceSignOffPolicy {
  signOffFrequency: SignOffFrequency;
  /**
   * Max. days a past absence can be audited.
   *
   * @type {number}
   * @memberof AbsenceSignOffPolicy
   */
  maxSignOffDaysPast: number;
  /**
   * Max. days a future absence can be audited.
   *
   * @type {number}
   * @memberof AbsenceSignOffPolicy
   */
  maxSignOffDaysFuture: number;
  historySummary: AbsenceHistorySummary;
}

/**
 * Defines special absence types.
 * Based on this, different UI can be shown.
 * E.g. show shortcuts like "15 minutes late" when type is "Late".
 *
 * @enum {number}
 */
enum AbsenceCategory {
  None,
  LeftEarly,
  Late,
}

/**
 * Describes an absence reason.
 * E.g.
 *
 * @export
 * @interface AbsenceReasonDefinition
 */
export interface AbsenceReasonDefinition {
  id: string;
  shortName: string;
  fullName: string;
  category: AbsenceCategory;
  /**
   * False when this absence reason does not count.
   * E.g.
   * - SV-Sitzung
   * - Bus ausgefallen...
   *
   * @type {boolean}
   * @memberof AbsenceReasonDefinition
   */
  includeInStatistics: boolean;
  /**
   * Defines if and how an absence reason will be automatically
   * resolved with an excuse definition.
   *
   * @type {ExcuseDefinition}
   * @memberof AbsenceReasonDefinition
   */
  autoExcuseDefinition?: ExcuseDefinition;
  isDisabled: boolean;
}

/**
 * Describes an excuse type.
 *
 * Example types:
 * - Verspätet
 * - Fehlt (ungeklärt)
 *
 * etc...
 *
 * @export
 * @interface ExcuseDefinition
 */
export type ExcuseDefinition = ExcuseDefinitionResponse;

/**
 * Defines a method of teaching.
 * Examples:
 *
 * - Frontalunterricht
 *
 * etc...
 *
 * @export
 * @interface MethodOfTeaching
 */
export interface MethodOfTeaching {
  id: string;
  name: string;
  description?: string;
  createdBy?: Person;
  isDisabled: boolean;
}

/**
 * Defines services.
 *
 * Examples:
 * - Klassensprecher:in
 * - Stufensprecher:in
 * - Sanitätsdienst
 * - Tafeldienst
 *
 * etc...
 *
 * @export
 * @interface ClassServiceDefinition
 */
export interface ClassServiceDefinition {
  id: string;
  shortName: string;
  fullName: string;
  description?: string;
  createdBy: Person;
  isDisabled: boolean;
}

/**
 * Describes a classbook entry definition category.
 * 
 * Examples:
 * - Verhalten
 * - Material

 * etc...
 *
 * @export
 * @interface ClassbookEntryDefinitionCategory
 */
export interface ClassbookEntryDefinitionCategory {
  id?: string;
  shortName: string;
  fullName: string;
  description?: string;
  isDisabled?: boolean;
}

/**
 * Define an entry definition like:
 *
 * - Lob
 * - Tadel
 *
 * etc...
 *
 * @export
 * @interface ClassbookEntryDefinition
 */
export interface ClassbookEntryDefinition {
  id: string;
  shortName: string;
  fullName: string;
  description?: string;
  category?: ClassbookEntryDefinitionCategory;
  isDisabled?: boolean;
}

export interface ClassbookSettingsGeneral {
  contactDetails: SchoolContactDetails;
  management: SchoolManagement;
  timePattern?: TimePattern;
  absenceSignOffPolicy: AbsenceSignOffPolicy;
  absenceReasons: AbsenceReasonDefinition[];
  excuseTypes: ExcuseDefinition[];
  teachingMethods: MethodOfTeaching[];
  serviceTypes: ClassServiceDefinition[];
  entryCategories: ClassbookEntryDefinitionCategory[];
  entryDefinitions: ClassbookEntryDefinition[];
}

/**
 * Defines who has to audit for passing the policy requirements.
 *
 * @enum {number}
 */
export enum SignOffRequirement {
  /**
   * No audition required.
   */
  none = 'None',
  /**
   * Any of the configured persons have to audit.
   */
  any = 'Any',
  /**
   * All of the configured persons have to audit.
   */
  all = 'All',
}

export interface GradeSettings {
  gradeManagers: Person[];
  gradeManagerIds?: string[];
  signOffRequirement: SignOffRequirement;
}

export interface TimeSlot {
  name: string;
  start: string;
  end: string;
  period?: number;
  isBreak?: boolean;
}

/**
 * Describes the source of the time pattern.
 *
 * @enum {number}
 */
export enum TimePatternSource {
  /**
   * Data is provided by an external source like Untis.
   * In this case, its not possible to edit via UI.
   */
  External = 'External',
  /**
   * Data was provided manually.
   */
  Manual = 'Manual',

  Unknown = 'Unknown',
}

export interface TimePattern {
  slots: TimeSlot[];
  breakSlots?: TimeSlot[];
  weekdays: Day[];
  source: TimePatternSource;
  isActive: boolean;
  id?: string;
}

/**
 * Defines, when a course takes place.
 *
 * @export
 * @interface CourseOccurrence
 */
export interface CourseOccurrence {
  slot: TimeSlot;
  weekday: Day;
}

export interface ClassCourse {
  id: string;
  name: string;
  classes: ClassBasicResponse[];
  azureGroup?: AzureGroup;
  color?: string;
  courseTeachers: CourseTeacher[];
  occurrences?: CourseOccurrence[];
}

export interface GradeClass {
  id: string;
  name: string;
  signOffRequirement: SignOffRequirement;
  azureGroup?: AzureGroup;
  classTeachers: Person[];
  courses: ClassCourse[];
}

export interface Grade {
  id: string;
  name: string;
  azureGroup?: AzureGroup;
  settings: GradeSettings;
  classes: GradeClass[];
}

export interface ClassbookSettings {
  general: ClassbookSettingsGeneral;
  grades: Grade[];
  /**
   * Can be set whenever a school want's another name
   * to be shown instead of "Grade".
   *
   * E.g.: Bereich
   *
   * @type {string}
   * @memberof ClassbookSettings
   */
  gradeAlias?: string;
}
